import "./index.css";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { AuthClientProvider } from "@/contexts/AuthClientContext";
import { ImpersonationProvider } from "@/contexts/impersonation-context";
import { ensureServiceWorkerRegistration } from "@/lib/service-worker";

// -----------------------------
// Build tag for debugging
// -----------------------------
const BUILD_TAG = "APP v40 — " + new Date().toISOString();
console.log("[MAIN] Starting", BUILD_TAG, "at path:", window.location.pathname);
console.log("[MAIN] Origin:", window.location.origin);

// -----------------------------
// iOS PWA standalone mode detection
// -----------------------------
const isStandalone =
  (window.navigator as any).standalone === true ||
  window.matchMedia("(display-mode: standalone)").matches;

const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

console.log("[MAIN] iOS:", isIOS, "| Standalone PWA:", isStandalone);
console.log("[MAIN] display-mode standalone:", window.matchMedia("(display-mode: standalone)").matches);
console.log("[MAIN] navigator.standalone:", (window.navigator as any).standalone);
console.log("[MAIN] User-Agent:", navigator.userAgent.substring(0, 120));

// Check if Auth0 tokens exist in localStorage (quick persistence check)
const hasAuth0Cache = Object.keys(localStorage).some(k => k.startsWith("@@auth0spajs@@"));
console.log("[MAIN] Auth0 localStorage cache found:", hasAuth0Cache, "| total localStorage keys:", localStorage.length);

if (isIOS && isStandalone) {
  console.warn("[MAIN] Running as iOS PWA in standalone mode. Cookie-based auth transactions are disabled to prevent cross-context PKCE failures.");
}

// -----------------------------
// ENV variables (Replit → window.__ENV__)
// -----------------------------
const env = (window as any).__ENV__ || {};
const AUTH0_DOMAIN = env.AUTH0_DOMAIN || "dev-gjkbcbcmtyz6soqa.us.auth0.com";
const AUTH0_CLIENT_ID =
  env.AUTH0_CLIENT_ID || "ehBNodbHZtmpisTMAwIQ4kJbtXL3DSHu";
const AUTH0_AUDIENCE = env.AUTH0_AUDIENCE || "https://showme.app/api";
const PUBLIC_BASE_URL = env.VITE_PUBLIC_BASE_URL || window.location.origin;
const REDIRECT_URI = `${PUBLIC_BASE_URL}/auth0/callback`;

console.log("[MAIN] Auth0 config:", {
  AUTH0_DOMAIN,
  AUTH0_CLIENT_ID,
  AUTH0_AUDIENCE,
  PUBLIC_BASE_URL,
  REDIRECT_URI,
});

// Keep the service worker available independently of authentication and UI
// route mounting. This does not request permission or create a subscription.
void ensureServiceWorkerRegistration()
  .then((registration) => {
    console.log(
      `[PushClientTrace] global serviceWorker registration scope=${registration.scope} ` +
      `activeState=${registration.active?.state || "none"}`,
    );
  })
  .catch((error) => {
    console.warn("[PushClientTrace] global serviceWorker registration failed:", error);
  });

// -----------------------------
// Auth0 client exposed globally
// -----------------------------
function Auth0ProviderWithClientExport({
  children,
}: {
  children: React.ReactNode;
}) {
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated, user } =
    useAuth0();

  React.useEffect(() => {
    (window as any).__auth0Client = {
      getAccessTokenSilently,
      getIdTokenClaims,
      isAuthenticated,
      user,
    };
    console.log("[Auth0] Client exposed globally for fetchWithAuth");
    console.log("[Auth0] isAuthenticated:", isAuthenticated, "| user sub:", user?.sub?.substring(0, 20));
  }, [isAuthenticated, user]);

  return <>{children}</>;
}

// -----------------------------
// MAIN RENDER
// -----------------------------
const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root #root not found");

ReactDOM.createRoot(rootEl).render(
  <>
    {/* Build Tag UI */}
    <div
      style={{
        position: "fixed",
        bottom: 6,
        right: 8,
        padding: "2px 6px",
        fontSize: 11,
        background: "#eee",
        borderRadius: 4,
        zIndex: 2147483647,
        opacity: 0.9,
        pointerEvents: "none",
      }}
      aria-hidden
    >
      {BUILD_TAG}
    </div>

    <QueryClientProvider client={queryClient}>
      <Auth0Provider
        domain={AUTH0_DOMAIN}
        clientId={AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: REDIRECT_URI,
          audience: AUTH0_AUDIENCE,
          scope: "openid profile email",
        }}
        cacheLocation="localstorage"
        useRefreshTokens
        onRedirectCallback={(appState) => {
          console.log("[Auth0Provider] onRedirectCallback — appState:", appState);
          const returnTo = appState?.returnTo || "/hubs";
          console.log("[Auth0Provider] Redirecting to:", returnTo, "| isStandalone:", isStandalone);
          window.history.replaceState({}, "", returnTo);
        }}
      >
        <Auth0ProviderWithClientExport>
          <AuthClientProvider>
            <ImpersonationProvider>
              {/* 🚫 No React.StrictMode to avoid double rendering in Replit/staging */}
              <App />
            </ImpersonationProvider>
          </AuthClientProvider>
        </Auth0ProviderWithClientExport>
      </Auth0Provider>
    </QueryClientProvider>
  </>
);
